'use client';

import { useCallback, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import dayjs from 'dayjs';
import type { ClassWithCourse } from '@peach/types/classes/class-public.dto.js';
import { tw } from '@tw';
import { rescheduleClass } from '@/app/actions/bookings';
import { canRescheduleClass, fromNow, hasEnded } from '@/lib/utils/date';
import { useClock } from '@/lib/hooks/useClock';
import { useCalendarStore } from '@/lib/stores/calendar.store';
import { countdown } from './countdown';
import { CALL_MOVE_CLOSED, CALL_MOVE_PICKING, callMoveOpen } from './policies';
import { SlotPicker } from './SlotPicker';
import { callAction } from '@/lib/call-action';

interface CallCardProps {
  cls: ClassWithCourse;
  tutorUsername: string;
  timezone: string | null;
  /** The server's clock at render, so the first paint can already count down. */
  serverNow: number;
  /** Laid under the intro card's headline, which already owns the card shell and names the zone. */
  embedded?: boolean;
}

const LIVE_WINDOW_MIN = 10;

/** The zone these times are written in, as the old picker said it: Europe/London. */
function zoneLabel(timezone: string): string {
  return timezone.replace(/_/g, ' ');
}

export function CallCard({
  cls,
  tutorUsername,
  timezone,
  serverNow,
  embedded = false,
}: CallCardProps) {
  const router = useRouter();
  const calendarChanged = useCalendarStore((s) => s.changed);
  const [moving, setMoving] = useState(false);
  const start = timezone ? dayjs(cls.start).tz(timezone) : dayjs(cls.start);
  const end = timezone ? dayjs(cls.end).tz(timezone) : dayjs(cls.end);

  // The clock ticks on the client and comes from the server before that, so it is
  // never zero. The zone can be, and everything that needs it waits behind the
  // skeleton below.
  const now = useClock(serverNow);

  // A call ends while the page stands open, so the card asks against the ticking
  // clock rather than trusting which list it was rendered into: the date square
  // and the standing line go quiet on their own the moment it passes.
  const past = hasEnded(cls.end, now);
  // `past` already carries "the end has gone by", so live is only the near side.
  const live = !past && now >= cls.start - LIVE_WINDOW_MIN * 60000;
  const movable = !past && canRescheduleClass(cls.start);
  // Whether the picker is on screen — asked once, because Join is the other half
  // of the same answer. Gating Join on `moving` alone let the two disagree: the
  // 24 hours can run out under an open picker, and the card was then left with
  // the picker gone and Join still suppressed by a flag nothing could clear.
  const picking = movable && moving;
  // One line under the time: where the call sits on the clock, and what can still
  // be done about it. The clock half is the zone while a call is far off — what a
  // printed hour means is the question a week out — and a countdown once it is
  // close, which is also when moving stops being possible.
  // Only ever read where the zone is known — the skeleton stands in until then.
  const move = moving
    ? CALL_MOVE_PICKING
    : callMoveOpen(start.subtract(24, 'hour').format('MMM D, HH:mm'));
  const standing = !timezone ? null : past ? (
    fromNow(cls.start)
  ) : live ? (
    'Live now'
  ) : movable ? (
    <>
      {embedded
        ? move.before.charAt(0).toUpperCase() + move.before.slice(1)
        : `${zoneLabel(timezone)} — ${move.before}`}{' '}
      <button
        type="button"
        onClick={() => setMoving((open) => !open)}
        aria-expanded={moving}
        aria-label={move.label}
        className="sf-note-act"
      >
        {move.action}
      </button>
      {move.after}
    </>
  ) : (
    `${countdown(cls.start - now)} — ${CALL_MOVE_CLOSED}`
  );

  const handleMove = useCallback(
    async (isoTime: string, pickedIn: string) => {
      if (dayjs(isoTime).isSame(dayjs(cls.start), 'minute')) {
        setMoving(false);
        return;
      }
      const result = await callAction(() => rescheduleClass(cls.id, isoTime, pickedIn));
      if (!result.success) return result.error;
      setMoving(false);
      calendarChanged(tutorUsername);
      router.refresh();
    },
    [cls.id, cls.start, router, calendarChanged, tutorUsername]
  );

  return (
    <div
      id={`sf-call-${cls.id}`}
      className={tw(
        !embedded &&
          'rounded-[var(--sf-radius)] border border-[var(--sf-line)] bg-[var(--sf-card)] px-3.5 py-3'
      )}
    >
      <div className="flex items-center gap-3.5">
        {!timezone ? (
          <div className="h-14 w-14 flex-shrink-0 animate-pulse rounded-xl bg-[var(--sf-line)]" />
        ) : (
          <div
            className={tw(
              'flex h-14 w-14 flex-shrink-0 flex-col items-center justify-center rounded-xl',
              past
                ? 'bg-[var(--sf-page)] text-[var(--sf-fnt)]'
                : live
                  ? 'bg-[var(--sf-lime)] text-[var(--sf-on-lime)]'
                  : 'bg-[var(--sf-ink)] text-[var(--sf-page)]'
            )}
            suppressHydrationWarning
          >
            <span className="sf-num text-head-1 font-extrabold leading-none">
              {start.format('D')}
            </span>
            <span className="sf-eyebrow mt-0.5 text-[10px] font-bold opacity-70">
              {start.format('MMM')}
            </span>
          </div>
        )}

        <div className="min-w-0 flex-1">
          {timezone ? (
            <>
              <span
                className="block sf-title sf-num text-title-2 font-extrabold text-[var(--sf-ink)]"
                suppressHydrationWarning
              >
                {start.format('HH:mm')} – {end.format('HH:mm')}
              </span>
              <span
                className={tw(
                  'block text-badge font-semibold',
                  live ? 'text-[var(--sf-ink)]' : 'text-[var(--sf-fnt)]'
                )}
                suppressHydrationWarning
              >
                {standing}
              </span>
            </>
          ) : (
            <>
              <span className="block h-6 w-36 animate-pulse rounded-md bg-[var(--sf-line)]" />
              <span className="mt-1.5 block h-3 w-24 animate-pulse rounded bg-[var(--sf-line)]" />
            </>
          )}
        </div>
      </div>

      {/* Join outlives the call itself: the room is not sealed at the end hour,
          and a client who dropped out at the last minute has one way back in.
          Opening the picker replaces it, so the card offers one action at a
          time and the times land under the line that promised them. */}
      {!picking && (
        <Link
          href={`/call/${cls.id}`}
          target="_blank"
          rel="noopener noreferrer"
          className={tw('mm-btn sf-cta mt-3', live ? 'sf-cta-lime' : 'sf-cta-ink')}
        >
          {live ? 'Join now' : 'Join'}
        </Link>
      )}

      {picking && (
        <SlotPicker
          tutorUsername={tutorUsername}
          calendar={{ bundleId: cls.course.bundle.id }}
          excludeClassId={cls.id}
          empty="No other times open right now — your call stays where it is"
          busyLabel="Moving…"
          serverNow={serverNow}
          zoneInTitle={embedded}
          label={(time) => {
            if (!time) return 'Pick a new time';
            if (time.isSame(dayjs(cls.start), 'minute')) return 'Keep this time';
            return `Move to ${time.format('ddd, MMM D')} at ${time.format('HH:mm')}`;
          }}
          onPick={handleMove}
        />
      )}
    </div>
  );
}
